import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile
} from 'firebase/auth'
import { Timestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { auth, functions } from '../config/firebase'
import { userService } from './firestore'
import { UserProfile, UserRegistrationForm, AuthState } from '../types/user'

class AuthService {
  // Register new user
  async registerUser(userData: UserRegistrationForm): Promise<UserProfile> {
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        userData.email, 
        userData.password
      )

      const user = userCredential.user

      // Update display name
      if (userData.displayName) {
        await updateProfile(user, {
          displayName: userData.displayName
        })
      }

      // Create user profile in Firestore
      const userProfile: UserProfile = {
        uid: user.uid,
        email: user.email!,
        emailVerified: false, // Require actual email verification
        displayName: userData.displayName,
        phoneNumber: userData.phoneNumber || '',
        companyName: userData.companyName || '',
        vatNumber: userData.vatNumber || '',
        address: userData.address || {
          street: '',
          city: '',
          postalCode: '',
          country: 'ZA'
        },
        createdAt: Timestamp.now(),
        lastLoginAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      }

      // Create the user profile in Firestore
      await userService.createUserProfile(user.uid, userProfile)

      // Note: Firebase Auth emailVerified will be updated via Cloud Function if needed

      // Store registration data for payment processing
      localStorage.setItem('pendingTrialData', JSON.stringify({
        planType: userData.planType,
        courts: userData.courts
      }))

      // Return the created profile
      return userProfile
    } catch (error: any) {
      console.error('Registration error:', error)
      throw new Error(this.getErrorMessage(error.code))
    }
  }

  // Sign in user
  async signIn(email: string, password: string): Promise<UserProfile> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const user = userCredential.user

      // Update last login time
      await userService.updateUserProfile(user.uid, {
        lastLoginAt: Timestamp.now()
      })

      // Get user profile
      const userProfile = await userService.getUserProfile(user.uid)
      if (!userProfile) {
        throw new Error('User profile not found')
      }
      return userProfile
    } catch (error: any) {
      console.error('Sign in error:', error)
      throw new Error(this.getErrorMessage(error.code))
    }
  }

  // Sign out user
  async signOut(): Promise<void> {
    try {
      await signOut(auth)
    } catch (error: any) {
      console.error('Sign out error:', error)
      throw new Error('Failed to sign out')
    }
  }

  // Send password reset email using custom Cloud Function
  async sendPasswordReset(email: string): Promise<void> {
    try {
      console.log('Attempting to send password reset email to:', email)
      const sendPasswordResetEmailFn = httpsCallable(functions, 'sendPasswordResetEmail')
      await sendPasswordResetEmailFn({ email })
      console.log('Password reset email sent successfully to:', email)
    } catch (error: any) {
      console.error('Password reset error:', error)
      // Handle Cloud Function errors
      if (error.code === 'functions/failed-precondition') {
        throw new Error('Email service is not configured. Please contact support.')
      }
      throw new Error(error.message || 'Failed to send password reset email. Please try again.')
    }
  }

  // Get user profile from Firestore
  async getUserProfile(uid: string): Promise<UserProfile> {
    try {
      const userProfile = await userService.getUserProfile(uid)
      if (!userProfile) {
        throw new Error('User profile not found')
      }
      return userProfile
    } catch (error: any) {
      console.error('Get user profile error:', error)
      throw new Error('Failed to get user profile')
    }
  }

  // Update user profile
  async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      await userService.updateUserProfile(uid, updates)
    } catch (error: any) {
      console.error('Update user profile error:', error)
      throw new Error('Failed to update user profile')
    }
  }

  // Get current user profile
  async getCurrentUserProfile(): Promise<UserProfile | null> {
    try {
      const user = auth.currentUser
      if (!user) {
        return null
      }
      return await this.getUserProfile(user.uid)
    } catch (error: any) {
      console.error('Get current user profile error:', error)
      return null
    }
  }

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: UserProfile | null) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          // Try to get existing user profile
          let userProfile = await userService.getUserProfile(firebaseUser.uid)
          
          // Always update emailVerified status from Firebase Auth
          if (userProfile) {
            userProfile.emailVerified = firebaseUser.emailVerified
          }
          
          // If profile doesn't exist, create a basic one
          if (!userProfile) {
            const basicProfile: UserProfile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email!,
              emailVerified: firebaseUser.emailVerified, // Use Firebase Auth email verification status
              displayName: firebaseUser.displayName || '',
              phoneNumber: firebaseUser.phoneNumber || '',
              companyName: '',
              vatNumber: '',
              address: {
                street: '',
                city: '',
                postalCode: '',
                country: 'ZA'
              },
              createdAt: Timestamp.now(),
              lastLoginAt: Timestamp.now(),
              updatedAt: Timestamp.now()
            }
            
            try {
              await userService.createUserProfile(firebaseUser.uid, basicProfile)
              userProfile = basicProfile
            } catch (error) {
              console.error('Failed to create basic user profile:', error)
              callback(null)
              return
            }
          }
          
          callback(userProfile)
        } catch (error) {
          console.error('Error getting user profile on auth state change:', error)
          callback(null)
        }
      } else {
        callback(null)
      }
    })
  }

  // Get user-friendly error messages
  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists'
      case 'auth/invalid-email':
        return 'Please enter a valid email address'
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled'
      case 'auth/weak-password':
        return 'Password should be at least 6 characters'
      case 'auth/user-disabled':
        return 'This account has been disabled'
      case 'auth/user-not-found':
        return 'No account found with this email'
      case 'auth/wrong-password':
        return 'Incorrect password'
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later'
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection'
      default:
        return 'An error occurred. Please try again'
    }
  }
}

export const authService = new AuthService() 